import axios, { AxiosRequestConfig } from 'axios';
import { runtimeConfig, serverRuntimeConfig } from 'config/runtimeConfig';
import {
  ContactsData,
  IPosition,
  IVacancyCvSearchParams,
  OpenContactsResponse,
} from 'data/interfaces/Common';
import { ICV, ICVCreate } from 'data/interfaces/ICV';
import { ProfileType } from 'data/interfaces/IRegistration';
import { IPagination } from 'types/types';
import request from 'utils/request';

export default class CvRepository {
  static async sugestCVPosition(s: string): Promise<IPosition[] | null> {
    const res = await request(`/api/cv/suggest?search=${s}`);
    if (res.err) {
      return null;
    }
    return res;
  }

  static async createCV(data: ICVCreate): Promise<any | null> {
    const res = await request({
      method: 'post',
      url: '/api/cv',
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getBlackListedByCurrentProfile(): Promise<IPagination<ICV> | null> {
    const res = await request({
      method: 'get',
      url: '/api/cv',
      data: {
        blackListedByCurrentProfile: true,
      },
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getById(data: number, profile?: ProfileType, token?: string): Promise<ICV | null> {
    const res = await request({
      method: 'get',
      url: `/api/cv/${data}`,
      profile,
      token,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async updateCV(id: number, data: ICVCreate): Promise<any | null> {
    const res = await request({
      method: 'patch',
      url: `/api/cv/${id}`,
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async deleteCV(id: number): Promise<any | null> {
    const res = await request({
      method: 'delete',
      url: `/api/cv/${id}`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getCVListOfCurrentUser(token: string): Promise<ICV[] | null> {
    const res = await request({
      method: 'get',
      url: '/api/cv/currentUser',
      token: token,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async getSavedByCurrentUser(): Promise<ICV[] | null> {
    const res = await request({
      method: 'get',
      url: '/api/cv',
      data: {
        savedByCurrentProfile: true,
      },
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async removeCvFromSaved(id: number, config?: AxiosRequestConfig): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/cv/${id}/removeFromSaved`,
      config,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async addCvToSaved(
    data: { id: number; favouriteFolderId?: number },
    config?: AxiosRequestConfig,
  ): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/cv/${data.id}/save`,
      data: data.favouriteFolderId ? { favouriteFolderId: data.favouriteFolderId } : {},
      config,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async findCvs(
    data: IVacancyCvSearchParams,
    page?: number,
    limit: number = 40,
  ): Promise<IPagination<ICV> | null> {
    const dataToSend: any = { ...data, page, limit, sort: 'createdAt,DESC', lang: 'en' };
    Object.entries(dataToSend).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        dataToSend[key] = value.join(',');
      }
    });
    if (data?.languageKnowledges && data.languageKnowledges.length > 0)
      dataToSend.languageKnowledges = JSON.stringify(
        data.languageKnowledges?.map((el) => {
          return { language: el.language, levels: el.levels };
        }),
      );
    const res = await request({
      method: 'get',
      url: '/api/cv',
      data: dataToSend,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async openContacts(id: number): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/cv/${id}/openContacts`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async addToBlackList(cvId: number): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/cv/${cvId}/blackList`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async removeFromBlackList(cvId: number): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/cv/${cvId}/removeFromBlackList`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async moveToTop(cvId: number): Promise<any | null> {
    const res = await request({
      method: 'get',
      url: `/api/cv/${cvId}/toTop`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async openContactsV2(
    cvId: number,
    productId: number,
  ): Promise<OpenContactsResponse | null> {
    const res = await request({
      method: 'post',
      profile: ProfileType.Hirer,
      url: `/api/cv/contacts/access/${cvId}/${productId}`,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async editContacts(
    cvId: number,
    profile: ProfileType,
    token: string,
    data: Omit<ContactsData, 'expiredAt'>,
  ): Promise<OpenContactsResponse | null> {
    const res = await request({
      method: 'post',
      url: `/api/cv/contacts/${cvId}`,
      profile,
      token,
      data,
    });
    if (res.err) {
      return null;
    }
    return res;
  }

  static async download(
    cvId: number,
    profile: ProfileType,
    token: string,
  ): Promise<ArrayBuffer | null> {
    const { HOST_INNER } = serverRuntimeConfig;
    const { HOST } = runtimeConfig;

    const res = await axios<ArrayBuffer>({
      method: 'get',
      baseURL: HOST_INNER ?? HOST,
      url: `api/cv/file/pdf/${cvId}`,
      responseType: 'arraybuffer',
      headers: {
        'Profile-Type': profile,
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.data) {
      return null;
    }

    return res.data;
  }
}
